import { Field } from "redux-form";
import { useMemo } from "react";
import { formValueSelector } from "redux-form";
import { useTypedSelector } from "App/Store";
import DatePicker from "shared/components/redux-form/DatePicker";
import dayjs from "dayjs";
import Select from "shared/components/redux-form/Select";
import InputWithLabel from "shared/components/redux-form/InputWithLabel";
import { onlyNumber } from "core/utils/Normalizer";
import { Bracket } from "core/models/Bracket";

interface ScheduleCommonBracketFormProps {
  brackets?: Bracket | null;
}

type RoundMetadata = {
  title: string;
  roundSequence: number;
  pendingMatches: number;
  region: string;
};

export default function ScheduleCommonBracketForm({
  brackets,
}: ScheduleCommonBracketFormProps) {
  const formatDate = (date: Date): Date | null => (date ? date : null);

  const selector = formValueSelector("common-schedule");

  const selectedRoundSequence = useTypedSelector((state) =>
    selector(state as any, "roundSequence")
  );

  const TIME_MIN = [
    { label: "1 min", value: 1 },
    { label: "5 min", value: 5 },
    { label: "10 min", value: 10 },
    { label: "15 min", value: 15 },
    { label: "30 min", value: 30 },
    { label: "60 min", value: 60 },
  ];

  const roundMetadata = useMemo(() => {
    const allRounds = [
      ...(brackets?.upper || []).map((round) => ({ round, region: "upper" })),
      ...(brackets?.lower || []).map((round) => ({ round, region: "lower" })),
      ...(brackets?.final || []).map((round) => ({ round, region: "final" })),
      ...(brackets?.thirdPlace || []).map((round) => ({
        round,
        region: "thirdPlace",
      })),
      ...(brackets?.roundRobin || []).map((round) => ({
        round,
        region: "roundRobin",
      })),
    ];

    const metadata = allRounds
      .map(({ round, region }) => {
        const seeds = round?.seeds || [];
        const roundSequence = Number(seeds?.[0]?.roundSequence);

        if (!roundSequence) return null;

        const pendingMatches = seeds.filter(
          (seed) => !seed?.isCompleted && !seed?.matchStart
        ).length;

        return {
          title: round?.title || `Round ${roundSequence}`,
          roundSequence,
          pendingMatches,
          region,
        };
      })
      .filter((entry): entry is RoundMetadata => Boolean(entry));

    return metadata.sort((a, b) => a.roundSequence - b.roundSequence);
  }, [brackets]);

  const roundOptions = roundMetadata.map((round) => {
    const regionLabels: Record<string, string> = {
      upper: `Upper Round ${round.roundSequence}`,
      lower: `Lower Round ${round.roundSequence}`,
      final: "Final",
      thirdPlace: "Third Place",
      roundRobin: `Round Robin ${round.roundSequence}`,
    };

    const regionText = regionLabels[round.region] || round.title;

    return {
      label: `${regionText} (${round.pendingMatches} pending)`,
      value: `${round.region}:${round.roundSequence}`,
    };
  });

  const maxMatchesForRound =
    roundMetadata.find(
      (round) =>
        `${round.region}:${round.roundSequence}` === selectedRoundSequence
    )?.pendingMatches || 0;

  return (
    <>
      <div className="qualifer-grid-form-100">
        <div className="qualifer-grid-form-wrapper spacing">
          <Field
            cleanable={false}
            searchable={false}
            name="roundSequence"
            component={Select}
            placeholder="Select Round"
            label="Round"
            options={roundOptions}
          />

          <Field
            name="matchesPerDay"
            component={InputWithLabel}
            placeholder={
              maxMatchesForRound
                ? `Enter Matches This Day (Max ${maxMatchesForRound})`
                : "Select round to set matches"
            }
            normalize={(value: string) =>
              onlyNumber(value, 1, Math.max(maxMatchesForRound, 1))
            }
            label="Matches This Day"
          />

          <Field
            name="startTime"
            dateFormat="DD-MM-YYYY HH:mm"
            min={dayjs().subtract(1, "day").toDate()}
            component={DatePicker}
            format={formatDate}
            placeholder="Select Start Time"
            label="Start Time"
          />

          <Field
            name="nextMatchGapMinutes"
            component={InputWithLabel}
            placeholder="Enter Next Match Gap"
            normalize={(value: string) => onlyNumber(value, 0, 1000)}
            label="Next Match Time Gap (In Minutes)"
          />

          <Field
            name="maxResultUpdateTime"
            component={InputWithLabel}
            placeholder="Enter Max Result Update Time"
            normalize={(value: string) => onlyNumber(value, 0, 1000)}
            label="Max Result Update Time (In Minutes)"
          />

          <Field
            cleanable={true}
            searchable={false}
            name="checkInMinutes"
            component={Select}
            placeholder="Select Check In Timer"
            label="Check In Timer (In Minutes)"
            options={TIME_MIN}
          />

          <Field
            cleanable={true}
            searchable={false}
            name="gracePeriodMinutes"
            component={Select}
            placeholder="Select Grace Time "
            label="Grace Time (In Minutes)"
            options={TIME_MIN}
          />
        </div>
      </div>
    </>
  );
}